import './Settings.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { Component } from 'react'
import { requestJSONwithCredentials, requestWithCredentials } from './request-json-with-credentials'
import SpinnerButton from './SpinnerButton'
import Spinner from './Spinner'
import ToggleButton from './ToggleButton'
import CopyToClipboardButton from './CopyToClipboardButton'
import * as R from 'ramda'
import scoreWeightDetails from './scoreWeights'
import Tracks from './Tracks'
import FollowItemButton from './FollowItemButton'
import { NavLink, withRouter } from 'react-router-dom'
import { apiURL } from './config'
import ExternalLink from './ExternalLink'
import Onboarding from './Onboarding'
import { SettingsHelp } from './SettingsHelp'
import { CartSelectorDropDownButton } from './CartSelectorDropDownButton'
import ImportPlaylistButton from './ImportPlaylistButton'
import FollowedItem from './FollowedItem'
import SearchBar from './SearchBar'
import ConfirmButton from './ConfirmButton'

const waitUntil = async (predicate, retryMs = 50) =>
  predicate()
    ? null
    : new Promise((resolve, reject) =>
        setTimeout(async () => {
          try {
            const result = await waitUntil(predicate, retryMs)
            resolve(result)
          } catch (e) {
            reject(e)
          }
        }, retryMs),
      )

const spotifyAuthorizationURL = `${apiURL}/auth/spotify?path=/settings/integrations`
const AuthorizationButtons = (props) => (
  <>
    <p>
      <span className={'input-layout'}>
        {!props.hasWriteAccess && (
          <a
            href={`${spotifyAuthorizationURL}&write=true`}
            className="button button-push_button button-push_button-small button-push_button-primary no-style-link"
          >
            Grant read and write access
          </a>
        )}
        {props.hasWriteAccess !== false && (
          <>
            <a
              href={`${spotifyAuthorizationURL}&write=false`}
              className="button button-push_button button-push_button-small button-push_button-primary no-style-link"
            >
              {props.hasWriteAccess === true ? 'Revoke write access' : 'Grant read-only access'}
            </a>
          </>
        )}
      </span>
      {props.hasWriteAccess === true && <RevokeWarning />}
    </p>
  </>
)

const RevokeWarning = () => (
  <div style={{ fontSize: '75%', marginTop: 5 }}>Warning! This will disable all cart synchronizations</div>
)

const FOLLOW_RESULT_CATEGORIES = ['artist', 'label', 'playlist']

// updatingFollowDetails is { [storeName]: { artist, label, playlist } } | null; flip
// one store's category to done, keeping null (a cleared search) as null.
const markCategoryDone = (updating, storeName, category) =>
  updating && { ...updating, [storeName]: { ...updating[storeName], [category]: false } }

class Settings extends Component {
  markHeardButton = (label, interval) => (
    <ConfirmButton
      disabled={this.state.markingHeard !== null}
      loading={this.state.markingHeard === interval}
      onClick={async () => {
        this.setState({ markingHeard: interval })
        await this.props.onMarkHeardClicked(interval)
        this.setState({ markingHeard: null })
      }}
      label={label}
      confirmLabel={`Confirm marking ${label.toLowerCase()} heard?`}
      processingLabel={'Marking heard'}
    />
  )

  constructor(props) {
    super(props)
    let page = window.location.pathname.split('/').pop()
    page = !page || page === 'settings' ? 'following' : page

    this.state = {
      followQuery: '',
      artistFollows: [],
      labelFollows: [],
      playlistFollows: [],
      followSuggestions: { artists: [], labels: [] },
      updatingSuggestion: null,
      artistOnLabelIgnores: [],
      artistIgnores: [],
      labelIgnores: [],
      notifications: this.props.notifications,
      notificationSearch: '',
      cartName: '',
      email: props.userSettings.email || '',
      updatingEmail: false,
      emailVerificationRequested: false,
      emailVerificationFailed: false,
      updatingFollowWithUrl: null,
      updatingFollowDetails: null,
      updatingCarts: false,
      updatingArtistOnLabelIgnores: false,
      updatingArtistIgnores: false,
      updatingLabelIgnores: false,
      updatingNotifications: false,
      updatingCartSync: null,
      addingCart: false,
      removingCart: false,
      followDetailsDebounce: undefined,
      followDetails: undefined,
      followSearchController: undefined,
      scoreWeightsDebounce: undefined,
      markingHeard: null,
      settingCartPublic: null,
      publicCarts: new Set(props.carts.filter(R.prop('isPublic')).map(R.prop('id'))),
      syncedCarts: Object.fromEntries(
        props.carts.filter(R.prop('storeDetails')).map(({ id, storeDetails }) => [id, storeDetails]),
      ),
      page,
      scoreWeights: this.props.scoreWeights,
      tracks: this.props.tracks,
      helpActive: false,
      importingPlaylist: null,
      importedPlaylists: [],
      importedArtists: [],
      exportingFollowedArtists: false,
      followedArtistsExportSuccess: null,
      authorizations: [],
      followedTab: 'artists',
      followResultsTab: 'artist',
      followedArtistsFilter: '',
      followedLabelsFilter: '',
      followedPlaylistsFilter: '',
      editingCartNameId: null,
      cartNameEditorValue: '',
      cloningCartId: null,
      mediaButtonBehavior: localStorage.getItem('mediaButtonBehavior') || 'seek',
      playPauseAddToCart: (localStorage.getItem('playPauseDoubleClickAddToCart') || 'true') === 'true',
      playPauseAddToCartDelay: parseInt(localStorage.getItem('playPauseDoubleClickDelay'), 10) || 1000,
      playPauseAddToCartCartId: localStorage.getItem('playPauseDoubleClickCartId') || '',
      audioSamples: [],
      uploadingAudioSample: false,
      deletingAudioSample: null,
      playingAudioSample: null,
    }

    this.markHeardButton.bind(this)
  }

  async componentDidMount() {
    try {
      await Promise.all([
        this.updateFollows(),
        this.updateFollowSuggestions(),
        this.updateIgnores(),
        this.updateAuthorizations(),
        this.updateAudioSamples(),
      ])
    } catch (e) {
      console.error(e)
    }

    this.setState({ loading: false })
  }

  async updateFollows() {
    await this.updateArtistFollows()
    await this.updateLabelFollows()
    await this.updatePlaylistFollows()
  }

  async updateArtistFollows() {
    const artistFollows = await requestJSONwithCredentials({
      path: `/me/follows/artists`,
    })

    this.setState({ artistFollows })
  }

  async updateLabelFollows() {
    const labelFollows = await requestJSONwithCredentials({
      path: `/me/follows/labels`,
    })
    this.setState({ labelFollows })
  }

  async updatePlaylistFollows() {
    const playlistFollows = await requestJSONwithCredentials({
      path: `/me/follows/playlists`,
    })
    this.setState({ playlistFollows })
  }

  async updateFollowSuggestions() {
    const followSuggestions = await requestJSONwithCredentials({
      path: `/me/follows/suggestions`,
    })
    this.setState({ followSuggestions })
    // Backfill cover images without blocking the list render.
    this.enrichSuggestionImages(followSuggestions)
  }

  async enrichSuggestionImages({ artists = [], labels = [] }) {
    const urls = [...artists, ...labels].map(({ url }) => url).filter(Boolean)
    if (urls.length === 0) {
      return
    }
    try {
      const images = await requestJSONwithCredentials({
        path: `/me/follows/suggestions/images`,
        method: 'POST',
        body: { urls },
      })
      const imgByUrl = new Map(images.filter(({ img }) => img).map(({ url, img }) => [url, img]))
      const merge = (items) => (items ?? []).map((s) => (imgByUrl.has(s.url) ? { ...s, img: imgByUrl.get(s.url) } : s))
      this.setState((prev) => ({
        followSuggestions: {
          ...prev.followSuggestions,
          artists: merge(prev.followSuggestions.artists),
          labels: merge(prev.followSuggestions.labels),
        },
      }))
    } catch (e) {
      console.error('Enriching suggestion images failed', e)
    }
  }

  async onFollowSuggestionClick(type, { id, name, url }) {
    this.setState({ updatingSuggestion: `${type}-${id}` })
    try {
      await this.onFollowItemClick(url, name, type)
      // Drop the now-followed suggestion and refresh from the server so the
      // counts and remaining suggestions stay in sync.
      this.removeSuggestionFromState(type, id)
      await this.updateFollowSuggestions()
    } catch (e) {
      console.error('Following suggestion failed', e)
    } finally {
      this.setState({ updatingSuggestion: null })
    }
  }

  async onIgnoreSuggestionClick(type, id) {
    this.setState({ updatingSuggestion: `${type}-${id}` })
    try {
      await requestWithCredentials({
        path: `/me/follows/suggestions/ignores`,
        method: 'POST',
        body: { type, id },
      })
      this.removeSuggestionFromState(type, id)
    } catch (e) {
      console.error('Ignoring suggestion failed', e)
    } finally {
      this.setState({ updatingSuggestion: null })
    }
  }

  removeSuggestionFromState(type, id) {
    const key = type === 'artist' ? 'artists' : 'labels'
    this.setState((prev) => ({
      followSuggestions: {
        ...prev.followSuggestions,
        [key]: (prev.followSuggestions[key] ?? []).filter((s) => s.id !== id),
      },
    }))
  }

  async updateIgnores() {
    await Promise.all([this.updateArtistIgnores(), this.updateLabelIgnores(), this.updateArtistOnLabelIgnores()])
  }

  async updateArtistIgnores() {
    const artistIgnores = await requestJSONwithCredentials({
      path: `/me/ignores/artists`,
    })
    this.setState({ artistIgnores })
  }

  async updateLabelIgnores() {
    const labelIgnores = await requestJSONwithCredentials({
      path: `/me/ignores/labels`,
    })
    this.setState({ labelIgnores })
  }

  async updateArtistOnLabelIgnores() {
    const artistOnLabelIgnores = await requestJSONwithCredentials({
      path: `/me/ignores/artists-on-labels`,
    })
    this.setState({ artistOnLabelIgnores })
  }

  async updateAuthorizations() {
    const authorizations = await requestJSONwithCredentials({
      path: `/me/authorizations`,
    })
    this.setState({ authorizations })
  }

  async updateAudioSamples() {
    try {
      const audioSamples = await requestJSONwithCredentials({
        path: `/me/notifications/audio-samples`,
      })
      this.setState({ audioSamples })
    } catch (e) {
      console.error('Failed to load audio samples', e)
    }
  }

  async uploadAudioSample(file) {
    if (!file) return

    const allowedTypes = ['audio/wav', 'audio/wave', 'audio/x-wav', 'audio/mpeg', 'audio/mp3']
    const allowedExts = ['.wav', '.mp3']
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

    if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
      alert('Invalid file type. Only WAV and MP3 files are allowed.')
      return
    }

    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      alert(`File size exceeds 10MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`)
      return
    }

    this.setState({ uploadingAudioSample: true })

    try {
      const formData = new FormData()
      formData.append('audioFile', file)

      const response = await fetch(`${apiURL}/me/notifications/audio-samples`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error(error.error || 'Upload failed')
      }

      await this.updateAudioSamples()
    } catch (e) {
      console.error('Failed to upload audio sample', e)
      alert(`Failed to upload audio sample: ${e.message}`)
    } finally {
      this.setState({ uploadingAudioSample: false })
    }
  }

  async deleteAudioSample(sampleId) {
    this.setState({ deletingAudioSample: sampleId })

    try {
      await requestWithCredentials({
        path: `/me/notifications/audio-samples/${sampleId}`,
        method: 'DELETE',
      })

      await this.updateAudioSamples()
    } catch (e) {
      console.error('Failed to delete audio sample', e)
      alert('Failed to delete audio sample')
    } finally {
      this.setState({ deletingAudioSample: null })
    }
  }

  async setCartPublic(cartId, setPublic) {
    this.setState({ settingCartPublic: cartId, updatingCarts: true })
    await requestWithCredentials({
      path: `/me/carts/${cartId}`,
      method: 'POST',
      body: {
        isPublic: setPublic,
      },
    })
    const updatedPublicCarts = new Set(this.state.publicCarts)
    setPublic ? updatedPublicCarts.add(cartId) : updatedPublicCarts.delete(cartId)
    this.setState({ settingCartPublic: null, updatingCarts: false, publicCarts: updatedPublicCarts })
  }

  async setCartSync(cartId, setSync) {
    this.setState({ updatingCartSync: cartId })
    try {
      const res = await requestWithCredentials({
        path: `/me/carts/${cartId}/sync/spotify`,
        method: 'POST',
        body: { setSync },
      })
      // The toggle and the playlist link read the cart's storeDetails entries, so store the entry the API returns.
      const others = (this.state.syncedCarts[cartId] || []).filter(({ storeName }) => storeName !== 'Spotify')
      const details = setSync ? [...others, await res.json()] : others
      const updatedSyncedCarts = { ...this.state.syncedCarts }
      details.length > 0 ? (updatedSyncedCarts[cartId] = details) : delete updatedSyncedCarts[cartId]
      this.setState({ syncedCarts: updatedSyncedCarts })
    } catch (e) {
      console.error('Failed to set cart sync status', e)
    } finally {
      this.setState({ updatingCartSync: null, updatingCarts: false })
    }
  }

  onShowPage(page) {
    this.setState({ page })
    this.props.history.push(`/settings/${page}`)
  }

  onShowFollowedTab(tab) {
    this.setState({ followedTab: tab })
  }

  onShowFollowResultsTab(tab) {
    this.setState({ followResultsTab: tab })
  }

  isFollowCategoryPending(type) {
    const updating = this.state.updatingFollowDetails
    return updating !== null && Object.values(updating).some((perStore) => perStore?.[type])
  }

  renderFollowResults() {
    const groupedByType = R.groupBy(R.prop('type'), this.state.followDetails ?? [])
    const tabs = [
      ['artist', 'Artists'],
      ['label', 'Labels'],
      ['playlist', 'Playlists'],
    ].filter(([type]) => (groupedByType[type] ?? []).length > 0 || this.isFollowCategoryPending(type))
    if (tabs.length === 0) return 'No results found'
    const activeTab = tabs.some(([type]) => type === this.state.followResultsTab)
      ? this.state.followResultsTab
      : tabs[0]?.[0]
    const items = groupedByType[activeTab] ?? []

    return (
      <>
        <div
          className="select-button select-button--container state-select-button--container noselect"
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          {tabs.map(([type, label]) => (
            <React.Fragment key={type}>
              <input
                type="radio"
                id={`settings-follow-results-${type}`}
                name="settings-follow-results"
                checked={activeTab === type}
                onChange={() => this.onShowFollowResultsTab(type)}
              />
              <label
                className="select_button-button  select_button-button__large"
                htmlFor={`settings-follow-results-${type}`}
              >
                {label}{' '}
                {this.isFollowCategoryPending(type) ? (
                  <Spinner size="small" />
                ) : (
                  <span className="follow-results-count">{(groupedByType[type] ?? []).length}</span>
                )}
              </label>
            </React.Fragment>
          ))}
        </div>
        {R.sortBy(
          R.prop(0),
          Object.entries(
            R.groupBy(
              R.propSatisfies(
                (name) => name.toLocaleLowerCase() !== this.state.followQuery.toLocaleLowerCase(),
                'name',
              ),
              items,
            ),
          ),
        ).map(([isNotExactMatch, grouped]) => (
          <React.Fragment key={`${activeTab}-${isNotExactMatch}`}>
            {activeTab !== 'playlist' && <h6>{isNotExactMatch === 'true' ? 'Related:' : 'Exact matches:'}</h6>}
            <ul className={'no-style-list follow-list'}>
              {grouped.map(({ id, name, store: { name: storeName }, type, url, img }) => (
                <li key={`${storeName}-${type}-${id ?? url}`}>
                  <FollowItemButton
                    id={id}
                    name={name}
                    storeName={storeName}
                    type={type}
                    url={url}
                    img={img}
                    disabled={this.getFollowItemDisabled(type, { id, url, storeName })}
                    loading={this.state.updatingFollowWithUrl === url}
                    onClick={(() => this.onFollowItemClick(url, name, type)).bind(this)}
                    data-onboarding-id="follow-item"
                  />
                </li>
              ))}
            </ul>
          </React.Fragment>
        ))}
      </>
    )
  }

  renderSuggestionList(type, suggestions) {
    const label = type === 'artist' ? 'Artists' : 'Labels'
    return (
      <>
        <h5>
          {label} ({suggestions.length})
        </h5>
        <ul className="no-style-list follow-list">
          {suggestions.map(({ id, name, url, storeName, count, img }) => {
            const busy = this.state.updatingSuggestion === `${type}-${id}`
            return (
              <li
                key={`${type}-${id}`}
                className="follow-suggestion-item"
                data-test="follow-suggestion"
                data-test-suggestion-type={type}
                data-test-suggestion-id={id}
                data-test-suggestion-name={name}
              >
                <FollowItemButton
                  id={id}
                  name={name}
                  storeName={storeName.toLowerCase()}
                  type={type}
                  url={url}
                  img={img}
                  loading={busy}
                  disabled={this.state.updatingSuggestion !== null}
                  onClick={() => this.onFollowSuggestionClick(type, { id, name, url })}
                  data-test="follow-suggestion-follow"
                />
                <span className="follow-suggestion-count" title={`${count} purchased track(s)`}>
                  {count}
                </span>
                <button
                  className="follow-suggestion-ignore-button"
                  disabled={this.state.updatingSuggestion !== null}
                  title={`Ignore suggestion for ${name}`}
                  data-test="follow-suggestion-ignore"
                  onClick={(e) => {
                    e.stopPropagation()
                    this.onIgnoreSuggestionClick(type, id)
                  }}
                >
                  <FontAwesomeIcon icon="times-circle" />
                </button>
              </li>
            )
          })}
        </ul>
      </>
    )
  }

  renderFollowSuggestions() {
    const { artists = [], labels = [] } = this.state.followSuggestions ?? {}
    if (artists.length === 0 && labels.length === 0) {
      return null
    }
    return (
      <div className="follow-suggestions" data-test="follow-suggestions">
        <h4>Suggestions from your purchases</h4>
        <p style={{ fontSize: '90%', marginTop: 0 }}>
          Artists and labels behind the tracks in your purchased cart that you are not following yet. Follow the ones
          you like, or ignore a suggestion to remove it from this list.
        </p>
        {artists.length > 0 && this.renderSuggestionList('artist', artists)}
        {labels.length > 0 && this.renderSuggestionList('label', labels)}
      </div>
    )
  }

  async setScoreWeight(property, value) {
    const newWeights = structuredClone(this.state.scoreWeights)
    newWeights.find(({ property: p }) => p === property).weight = value
    this.setState({ scoreWeights: newWeights })

    if (this.state.scoreWeightsDebounce) {
      clearTimeout(this.state.scoreWeightsDebounce)
      this.setState({
        scoreWeightsDebounce: undefined,
      })
    }

    const timeout = setTimeout(async () => {
      await requestWithCredentials({
        path: `/me/score-weights`,
        method: 'POST',
        body: newWeights,
      })

      try {
        this.setState({ updatingTracks: true })

        const { tracks } = await requestJSONwithCredentials({ path: `/me/tracks/new?limit=10` })

        this.setState({ tracks: { ...this.state.tracks, new: tracks }, updatingTracks: false })
      } catch (e) {
        console.error(e)
        this.setState({ updatingTracks: false })
      }
    }, 500)

    this.setState({ scoreWeightsDebounce: timeout })
  }

  clearSearch() {
    if (this.state.followSearchController) {
      this.state.followSearchController.abort()
    }
    this.setState({
      followQuery: '',
      updatingFollowDetails: null,
      followDetails: undefined,
      updatingFollowWithUrl: null,
      followSearchController: undefined,
    })
  }

  renderWeightInputs({ property, weight }) {
    const weightDetails = scoreWeightDetails[property]
    const numberProps = { min: weightDetails.min, max: weightDetails.max, step: weightDetails.step }
    const scaling = weightDetails.isPenalty ? -1 : 1
    const scaledWeight = scaling * weight
    return (
      <div style={{ display: 'table-row', margin: 5 }} key={property} className={'input-layout'}>
        <label
          style={{ display: 'table-cell', fontSize: '90%', verticalAlign: 'middle' }}
          htmlFor={`weights-${property}`}
        >
          {weightDetails.label}
        </label>
        <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
          <input
            id={`weights-${property}`}
            type="range"
            style={{
              display: 'table-cell',
            }}
            value={scaledWeight}
            onChange={(e) => this.setScoreWeight(property, Number(e.target.value) * scaling)}
            {...numberProps}
          />
        </div>
        <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
          <input
            type="number"
            value={scaledWeight}
            onChange={(e) => this.setScoreWeight(property, Number(e.target.value) * scaling)}
            style={{ display: 'table-cell' }}
            className={'text-input text-input-small text-input-dark '}
            {...numberProps}
          />
        </div>
        <div style={{ display: 'table-cell', fontSize: '60%', verticalAlign: 'middle' }}>
          <div style={{ marginLeft: 6 }}>{weightDetails.unit}</div>
        </div>
      </div>
    )
  }

  componentDidUpdate({ location: { pathname: prevPath } }) {
    const newPath = this.props.location.pathname
    if (prevPath !== newPath && this.props.location.pathname.startsWith('/settings')) {
      this.setState({ page: newPath.split('/').pop() || 'following' })
    }
  }

  async renameCart(id, name) {
    this.setState({ updatingCartNameId: id })
    await requestWithCredentials({ path: `/me/carts/${id}`, method: 'POST', body: { name } })
    await this.props.onUpdateCarts()
    this.setState({ updatingCartNameId: null, editingCartNameId: null })
  }

  async cloneCart(id) {
    this.setState({ cloningCartId: id })

    try {
      // The cart is returned a page at a time; the copy gets every track.
      const trackIds = []
      let cart
      for (;;) {
        const response = await requestJSONwithCredentials({ path: `/me/carts/${id}?offset=${trackIds.length}&limit=500` })
        cart = response.cart
        trackIds.push(...response.tracks.map((track) => track.id))
        if (response.tracks.length === 0 || trackIds.length >= response.page.total) break
      }
      const createdCart = await requestJSONwithCredentials({
        path: `/me/carts`,
        method: 'POST',
        body: { name: `${cart.name} - Copy`, tracks: trackIds.map((trackId) => ({ trackId })) },
      })
      await this.props.onUpdateCarts()
      if (createdCart) {
        const cartId = `cart-${createdCart.uuid}`

        await waitUntil(() => {
          const cartElement = document.getElementById(cartId)
          if (!cartElement) return false
          cartElement.scrollIntoView({ behavior: 'smooth' })
          return true
        })
      }
    } catch (e) {
      console.error('Cloning cart failed', e)
    }
    this.setState({ cloningCartId: null })
  }

  render() {
    const spotifyAuthorization = this.state.authorizations?.find(R.propEq('Spotify', 'store_name'))
    return (
      <div className="page-container scroll-container" style={{ paddingBottom: 16, ...this.props.style }}>
        <SettingsHelp
          active={this.state.helpActive}
          onActiveChanged={(active) => this.setState({ helpActive: active })}
        />
        <div className="settings-container">
          <h2>
            Settings{' '}
            <FontAwesomeIcon
              icon="circle-question"
              style={{ fontSize: '50%', verticalAlign: 'top' }}
              onClick={() => {
                this.setState({ helpActive: !this.state.helpActive })
              }}
              data-onboarding-id="help-button"
            />
          </h2>
          <div>
            <div className="select-button select-button--container state-select-button--container noselect">
              <input
                type="radio"
                id="settings-state-following"
                name="settings-state"
                checked={this.state.page === 'following'}
                onChange={() => this.onShowPage('following')}
              />
              <label
                className="select_button-button  select_button-button__large"
                htmlFor="settings-state-following"
                data-help-id="following-tab"
              >
                Following
              </label>
              <input
                type="radio"
                id="settings-state-carts"
                name="settings-state"
                checked={this.state.page === 'carts'}
                onChange={() => this.onShowPage('carts')}
              />
              <label
                className="select_button-button  select_button-button__large"
                htmlFor="settings-state-carts"
                data-help-id="carts-tab"
              >
                Carts
              </label>
              <input
                type="radio"
                id="settings-state-notifications"
                name="settings-state"
                checked={this.state.page === 'notifications'}
                onChange={() => this.onShowPage('notifications')}
              />
              <label
                className="select_button-button select_button-button__large"
                htmlFor="settings-state-notifications"
                data-help-id="notifications-tab"
              >
                Notifications
              </label>
              <input
                type="radio"
                id="settings-state-player"
                name="settings-state"
                checked={this.state.page === 'player'}
                onChange={() => this.onShowPage('player')}
              />
              <label
                className="select_button-button select_button-button__large"
                htmlFor="settings-state-player"
                data-help-id="player-tab"
              >
                Player
              </label>
              <input
                type="radio"
                id="settings-state-ignores"
                name="settings-state"
                checked={this.state.page === 'ignores'}
                onChange={() => this.onShowPage('ignores')}
              />
              <label
                className="select_button-button select_button-button__large"
                htmlFor="settings-state-ignores"
                data-help-id="ignores-tab"
              >
                Ignores
              </label>
              <input
                type="radio"
                id="settings-state-sorting"
                name="settings-state"
                checked={this.state.page === 'sorting'}
                onChange={() => this.onShowPage('sorting')}
              />
              <label
                className="select_button-button  select_button-button__large"
                htmlFor="settings-state-sorting"
                data-help-id="sorting-tab"
              >
                Sorting
              </label>
              <input
                type="radio"
                id="settings-state-collection"
                name="settings-state"
                checked={this.state.page === 'collection'}
                onChange={() => this.onShowPage('collection')}
              />
              <label
                className="select_button-button select_button-button__large"
                htmlFor="settings-state-collection"
                data-help-id="collection-tab"
              >
                Collection
              </label>
              {this.props.stores.includes('spotify') && (
                <>
                  <input
                    type="radio"
                    id="settings-state-integrations"
                    name="settings-state"
                    checked={this.state.page === 'integrations'}
                    onChange={() => this.onShowPage('integrations')}
                  />
                  <label
                    className="select_button-button select_button-button__large"
                    htmlFor="settings-state-integrations"
                    data-help-id="integrations-tab"
                  >
                    Integrations
                  </label>
                </>
              )}
            </div>
          </div>
          {this.state.page === 'following' ? (
            <>
              <label>
                <h4>Search by name or URL to follow:</h4>
                <div className="input-layout" style={{ maxWidth: '40ch' }} data-onboarding-id="follow-search">
                  <SearchBar
                    disabled={this.state.updatingFollowWithUrl !== null}
                    styles="large dark"
                    value={this.state.followQuery}
                    onClearSearch={this.clearSearch.bind(this)}
                    onChange={({ target: { value: inputValue } }) => {
                      this.setState({
                        followQuery: inputValue,
                        followDetails: undefined,
                        updatingFollowDetails: null,
                      })
                      if (this.state.followDetailsDebounce) {
                        clearTimeout(this.state.followDetailsDebounce)
                        this.setState({ followDetailsDebounce: undefined })
                      }
                      // Supersede any in-flight search so its (possibly late) results
                      // can't be appended to a newer query's results.
                      if (this.state.followSearchController) {
                        this.state.followSearchController.abort()
                      }

                      if (inputValue === '') {
                        this.setState({ followSearchController: undefined })
                        return
                      }
                      this.setState({
                        updatingFollowDetails: Object.fromEntries(
                          this.props.stores.map(({ storeName }) => [
                            storeName,
                            Object.fromEntries(FOLLOW_RESULT_CATEGORIES.map((category) => [category, true])),
                          ]),
                        ),
                      })
                      const controller = new AbortController()
                      const { signal } = controller
                      const timeout = setTimeout(async () => {
                        if (this.state.followQuery.match('^https://')) {
                          try {
                            const results = await (
                              await requestWithCredentials({
                                path: `/followDetails?q=${encodeURIComponent(this.state.followQuery)}`,
                                signal,
                              })
                            ).json()
                            if (signal.aborted) return
                            this.setState({ followDetails: results, updatingFollowDetails: null })
                          } catch (e) {
                            if (e.name === 'AbortError') return
                            console.error('Error updating follow details', e)
                            this.setState({ updatingFollowDetails: null, followDetailsDebounce: undefined })
                          }
                        } else {
                          // One request per (store, category) so each category's results
                          // render as soon as its request completes, independently.
                          const requests = this.props.stores.flatMap(({ storeName }) =>
                            FOLLOW_RESULT_CATEGORIES.map((category) =>
                              requestWithCredentials({
                                path: `/stores/${storeName}/search/?q=${encodeURIComponent(
                                  this.state.followQuery,
                                )}&type=${category}`,
                                signal,
                              })
                                .then(async (res) =>
                                  (await res.json()).map((result) => ({ stores: [storeName], ...result })),
                                )
                                .then((json) => {
                                  if (signal.aborted) return
                                  this.setState((prev) => ({
                                    followDetails: R.uniqBy(
                                      R.prop('url'),
                                      R.sortBy(
                                        R.compose(R.toLower, R.prop('name')),
                                        (prev.followDetails ?? []).concat(json),
                                      ),
                                    ),
                                    updatingFollowDetails: markCategoryDone(
                                      prev.updatingFollowDetails,
                                      storeName,
                                      category,
                                    ),
                                  }))
                                })
                                .catch((e) => {
                                  if (signal.aborted || e.name === 'AbortError') return
                                  console.error('Error updating follow details', e)
                                  this.setState((prev) => ({
                                    updatingFollowDetails: markCategoryDone(
                                      prev.updatingFollowDetails,
                                      storeName,
                                      category,
                                    ),
                                  }))
                                }),
                            ),
                          )
                          Promise.all(requests).finally(() => {
                            if (!signal.aborted && this.state.followDetails?.length !== 0) {
                              if (Onboarding.active && Onboarding.isCurrentStep(Onboarding.steps.Search)) {
                                return setTimeout(() => Onboarding.helpers.next(), 500)
                              }
                            }
                          })
                        }
                      }, 500)
                      this.setState({ followDetailsDebounce: timeout, followSearchController: controller })
                    }}
                  />
                </div>
                {this.state.followQuery.match('^https://') && this.state.followDetails === undefined ? (
                  this.state.updatingFollowDetails !== null && (
                    <>
                      <br />
                      Searching <Spinner size="large" />
                    </>
                  )
                ) : this.state.updatingFollowDetails === null && this.state.followDetails === undefined ? null : (
                  <>{this.renderFollowResults()}</>
                )}
                {this.props.stores.includes('spotify') && (
                  <div style={{ fontSize: '75%', marginTop: 5 }}>
                    <a
                      href=""
                      onClick={(e) => {
                        e.preventDefault()
                        this.setState({ page: 'integrations' })
                      }}
                    >
                      To import followed artists from Spotify use the integrations tab
                    </a>
                  </div>
                )}
              </label>
              {this.renderFollowSuggestions()}
              <h4>Followed</h4>
              <div
                className="select-button select-button--container state-select-button--container noselect"
                style={{ marginBottom: 16 }}
              >
                <input
                  type="radio"
                  id="settings-state-following-artists"
                  name="settings-state-following"
                  checked={this.state.followedTab === 'artists'}
                  onChange={() => this.onShowFollowedTab('artists')}
                />
                <label
                  className="select_button-button  select_button-button__large"
                  htmlFor="settings-state-following-artists"
                >
                  Artists ({this.state.artistFollows.length})
                </label>
                <input
                  type="radio"
                  id="settings-state-following-labels"
                  name="settings-state-following"
                  checked={this.state.followedTab === 'labels'}
                  onChange={() => this.onShowFollowedTab('labels')}
                />
                <label
                  className="select_button-button  select_button-button__large"
                  htmlFor="settings-state-following-labels"
                >
                  Labels ({this.state.labelFollows.length})
                </label>
                <input
                  type="radio"
                  id="settings-state-following-playlists"
                  name="settings-state-following"
                  checked={this.state.followedTab === 'playlists'}
                  onChange={() => this.onShowFollowedTab('playlists')}
                />
                <label
                  className="select_button-button  select_button-button__large"
                  htmlFor="settings-state-following-playlists"
                >
                  Playlists ({this.state.playlistFollows.length})
                </label>
              </div>
              {this.state.followedTab === 'artists' && (
                <>
                  <div className={'follow-filter'}>
                    <SearchBar
                      styles="large dark"
                      onChange={({ target: { value } }) =>
                        this.setState({ followedArtistsFilter: value.toLocaleLowerCase() })
                      }
                      value={this.state.followedArtistsFilter}
                      onClearSearch={() => this.setState({ followedArtistsFilter: '' })}
                    />
                  </div>
                  <ul className="no-style-list follow-list">
                    {this.state.artistFollows
                      .filter(
                        ({ name }) =>
                          this.state.followedArtistsFilter === '' ||
                          name.toLocaleLowerCase().includes(this.state.followedArtistsFilter),
                      )
                      .map(({ name, storeArtistId, store: { name: storeName, starred, watchId }, url }) => {
                        return (
                          <li key={storeArtistId} data-onboarding-id="follow-item">
                            <FollowedItem
                              disabled={this.state.updatingArtistFollows}
                              onStarClick={async (e) => {
                                e.stopPropagation()
                                this.setState({ updatingArtistFollows: true })
                                await this.props.onSetStarred('artists', watchId, !starred)
                                await this.updateArtistFollows()
                                this.setState({ updatingArtistFollows: false })
                                if (Onboarding.active && Onboarding.isCurrentStep(Onboarding.steps.Star)) {
                                  setTimeout(() => Onboarding.helpers.next(), 500)
                                }
                              }}
                              onUnfollowClick={async () => {
                                this.setState({ updatingArtistFollows: true })
                                await requestWithCredentials({
                                  path: `/me/follows/artists/${storeArtistId}`,
                                  method: 'DELETE',
                                })
                                await this.updateArtistFollows()
                                this.setState({ updatingArtistFollows: false })
                                if (Onboarding.active && Onboarding.isCurrentStep(Onboarding.steps.Unfollow)) {
                                  setTimeout(() => {
                                    document.querySelector('[data-onboarding-id=support-button]').scrollIntoView()
                                    Onboarding.helpers.next()
                                  }, 500)
                                }
                              }}
                              {...{ storeName, title: name, starred, url }}
                            />
                          </li>
                        )
                      })}
                  </ul>
                </>
              )}
              {this.state.followedTab === 'labels' && (
                <>
                  <div className={'follow-filter'}>
                    <SearchBar
                      styles="large dark"
                      onChange={({ target: { value } }) =>
                        this.setState({ followedLabelsFilter: value.toLocaleLowerCase() })
                      }
                      value={this.state.followedLabelsFilter}
                      onClearSearch={() => this.setState({ followedLabelsFilter: '' })}
                    />
                  </div>
                  <ul className="no-style-list follow-list">
                    {this.state.labelFollows
                      .filter(
                        ({ name }) =>
                          this.state.followedLabelsFilter === '' ||
                          name.toLocaleLowerCase().includes(this.state.followedLabelsFilter),
                      )
                      .map(({ name, url, storeLabelId, store: { name: storeName, watchId, starred } }, index) => (
                        <li key={`${storeLabelId ?? url ?? watchId ?? name}-${index}`}>
                          <FollowedItem
                            disabled={this.state.updatingLabelFollows}
                            onStarClick={async (e) => {
                              e.stopPropagation()
                              this.setState({ updatingLabelFollows: true })
                              await this.props.onSetStarred('labels', watchId, !starred)
                              await this.updateLabelFollows()
                              this.setState({ updatingLabelFollows: false })
                            }}
                            onUnfollowClick={async () => {
                              this.setState({ updatingLabelFollows: true })
                              await requestWithCredentials({
                                path: `/me/follows/labels/${storeLabelId}`,
                                method: 'DELETE',
                              })
                              await this.updateLabelFollows()
                              this.setState({ updatingLabelFollows: false })
                            }}
                            {...{ storeName, title: name, starred, url }}
                          />
                        </li>
                      ))}
                  </ul>
                </>
              )}
              {this.state.followedTab === 'playlists' && (
                <>
                  <div className={'follow-filter'}>
                    <SearchBar
                      styles="large dark"
                      onChange={({ target: { value } }) =>
                        this.setState({ followedPlaylistsFilter: value.toLocaleLowerCase() })
                      }
                      value={this.state.followedPlaylistsFilter}
                      onClearSearch={() => this.setState({ followedPlaylistsFilter: '' })}
                    />
                  </div>
                  <ul className="no-style-list follow-list">
                    {this.state.playlistFollows
                      .filter(
                        ({ name }) =>
                          this.state.followedPlaylistsFilter === '' ||
                          name.toLocaleLowerCase().includes(this.state.followedPlaylistsFilter),
                      )
                      .map(({ id, storeName, title }) => (
                        <li key={id}>
                          <FollowedItem
                            disabled={this.state.updatingPlaylistFollows}
                            onUnfollowClick={async () => {
                              this.setState({ updatingPlaylistFollows: true })
                              await requestWithCredentials({
                                path: `/me/follows/playlists/${id}`,
                                method: 'DELETE',
                              })
                              await this.updatePlaylistFollows()
                              this.setState({ updatingPlaylistFollows: false })
                            }}
                            {...{ storeName, title }}
                          />
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </>
          ) : null}
          {this.state.page === 'sorting' ? (
            <>
              <p>
                The track list is sorted according to the following weights. The purpose of the weights is to higlight
                the most relevant releases.
              </p>
              <label>
                <h4>Weights:</h4>
                <h5>Bonuses:</h5>
                <div style={{ display: 'table' }}>
                  {this.state.scoreWeights
                    .filter(({ property }) => !scoreWeightDetails[property].isPenalty)
                    .map(this.renderWeightInputs.bind(this))}
                </div>
                <h5>Penalties:</h5>
                <div style={{ display: 'table' }}>
                  {this.state.scoreWeights
                    .filter(({ property }) => scoreWeightDetails[property].isPenalty)
                    .map(this.renderWeightInputs.bind(this))}
                </div>
              </label>
              <h5>Preview:</h5>
              <div style={{ border: '1px solid black' }}>
                <Tracks
                  mode={'list'}
                  tracks={this.state.tracks.new.slice(0, 10) || []}
                  listState={'new'}
                  notifications={[]}
                  carts={[]}
                  height={380}
                  loading={this.state.updatingTracks}
                  onPreviewRequested={() => {}}
                  follows={this.props.follows}
                  stores={this.props.stores}
                  preview={true}
                />
              </div>
            </>
          ) : null}
          {this.state.page === 'carts' ? (
            <>
              <label>
                <h4>Create a new cart:</h4>
                <div className="input-layout">
                  <input
                    className="text-input text-input-large text-input-dark"
                    disabled={this.state.updatingCarts}
                    value={this.state.cartName}
                    onChange={(e) => this.setState({ cartName: e.target.value })}
                  />
                  <SpinnerButton
                    className="button button-push_button-large button-push_button-primary"
                    loading={this.state.addingCart}
                    disabled={this.state.cartName === '' || this.state.addingCart || this.state.updatingCarts}
                    label="Create"
                    loadingLabel="Creating"
                    onClick={async () => {
                      this.setState({ updatingCarts: true, addingCart: true })
                      try {
                        await this.props.onCreateCart(this.state.cartName)
                        this.setState({ cartName: '' })
                        await this.props.onUpdateCarts()
                      } catch (e) {
                        console.error(e)
                      }
                      this.setState({ updatingCarts: false, addingCart: false })
                    }}
                  />
                </div>
              </label>
              <h3>Carts</h3>
              <div>
                {this.props.carts.map(({ id, isDefault, isPublic, isPurchased, name, uuid }, index) => {
                  const buttonId = `sharing-${id}`
                  const cartPath = `/carts/${uuid}`
                  const publicLink = new URL(cartPath, window.location).toString()
                  const spotifyStoreDetails = this.state.syncedCarts[id]?.find(
                    ({ storeName }) => storeName === 'Spotify',
                  )
                  return (
                    <div key={id} id={`cart-${uuid}`}>
                      <h4
                        style={{
                          marginTop: index === 0 ? '1rem' : '3rem',
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        {this.state.editingCartNameId === id ? (
                          <>
                            <input
                              style={{ fontSize: '1rem', padding: 9 }}
                              disabled={this.state.updatingCartNameId}
                              value={this.state.cartNameEditorValue}
                              className={'text-input text-input-small text-input-dark'}
                              onChange={(e) => this.setState({ cartNameEditorValue: e.target.value })}
                            />{' '}
                            <SpinnerButton
                              loading={this.state.updatingCartNameId}
                              size={'small'}
                              onClick={() => this.renameCart(id, this.state.cartNameEditorValue)}
                            >
                              <FontAwesomeIcon icon={'save'} /> Save
                            </SpinnerButton>{' '}
                            <SpinnerButton
                              disabled={this.state.updatingCartNameId}
                              size={'small'}
                              onClick={() => this.setState({ editingCartNameId: null })}
                            >
                              <FontAwesomeIcon icon={'cancel'} /> Cancel
                            </SpinnerButton>
                          </>
                        ) : (
                          <>
                            <NavLink to={cartPath}>{name}</NavLink>{' '}
                            {!isDefault && !isPurchased && (
                              <>
                                <button
                                  className={
                                    'button button-push_button button-push_button-small button-push_button-primary'
                                  }
                                  onClick={() => this.setState({ editingCartNameId: id, cartNameEditorValue: name })}
                                >
                                  <FontAwesomeIcon icon={'pen'} /> Rename
                                </button>{' '}
                                <ConfirmButton
                                  icon={'trash'}
                                  loading={this.state.deletingCart === id}
                                  disabled={
                                    this.state.addingCart || this.state.updatingCarts || this.state.deletingCart
                                  }
                                  onClick={async () => {
                                    this.setState({ deletingCart: id, updatingCarts: true })
                                    await requestWithCredentials({ path: `/me/carts/${id}`, method: 'DELETE' })
                                    await this.props.onUpdateCarts()
                                    this.setState({ deletingCart: null, updatingCarts: false })
                                  }}
                                  label="Delete"
                                  confirmLabel={`Confirm deletion of "${name}"?`}
                                  processingLabel={'Deleting'}
                                />
                              </>
                            )}
                            <SpinnerButton
                              disabled={this.state.cloningCartId}
                              loading={this.state.cloningCartId === id}
                              icon={'clone'}
                              onClick={() => this.cloneCart(id)}
                            >
                              Clone
                            </SpinnerButton>
                          </>
                        )}
                      </h4>
                      <div>
                        <p style={{ display: 'flex', alignItems: 'center', gap: 16 }} className="input-layout">
                          <label htmlFor={buttonId} className="noselect">
                            Sharing enabled:
                          </label>
                          <ToggleButton
                            id={buttonId}
                            checked={isPublic}
                            disabled={this.state.settingCartPublic !== null || this.state.updatingCarts}
                            onChange={(state) => this.setCartPublic(id, state)}
                          />
                          {this.state.settingCartPublic === id ? <Spinner size="small" /> : null}
                          {this.state.publicCarts.has(id) ? (
                            <CopyToClipboardButton content={publicLink} label={'Copy link'} />
                          ) : null}
                        </p>
                      </div>
                      {this.props.stores.includes('spotify') &&
                        (!spotifyAuthorization ? (
                          <p>
                            Grant Fomo Player access to Spotify from the{' '}
                            <a
                              style={{ textDecoration: 'underline' }}
                              onClick={this.onShowPage.bind(this, 'integrations')}
                            >
                              Integrations tab
                            </a>{' '}
                            to enable synchronization
                          </p>
                        ) : (
                          <>
                            <p className="input-layout" style={{ display: 'flex', alignItems: 'center' }}>
                              <label htmlFor={`${uuid}-sync`} className="noselect">
                                Spotify sync enabled:
                              </label>
                              <ToggleButton
                                id={`${uuid}-sync`}
                                checked={spotifyStoreDetails}
                                disabled={this.state.updatingCartSync !== null || this.state.updatingCarts}
                                onChange={(state) => this.setCartSync(id, state)}
                              />
                              {this.state.updatingCartSync === id ? <Spinner size="small" /> : null}
                            </p>
                            {spotifyStoreDetails && (
                              <p>
                                <ExternalLink href={spotifyStoreDetails.url}>Open Spotify playlist</ExternalLink>
                              </p>
                            )}
                          </>
                        ))}
                    </div>
                  )
                })}
              </div>
            </>
          ) : null}
          {this.state.page === 'notifications' ? (
            <>
              <label>
                <h4>Email address:</h4>
                <div className="input-layout">
                  <input
                    type={'email'}
                    className="text-input text-input-large text-input-dark"
                    value={this.state.email}
                    onChange={(e) => this.setState({ email: e.target.value, emailVerificationRequested: false })}
                  />
                  <SpinnerButton
                    size={'large'}
                    disabled={
                      this.state.updatingEmail ||
                      (this.props.userSettings.email === this.state.email && this.props.userSettings.emailVerified) ||
                      this.state.email === ''
                    }
                    loading={this.state.updatingEmail}
                    onClick={async () => {
                      this.setState({
                        updatingEmail: true,
                        emailVerificationRequested: true,
                        emailVerificationFailed: false,
                      })
                      try {
                        await this.props.onUpdateEmail(this.state.email)
                      } catch (e) {
                        this.setState({ emailVerificationFailed: true })
                      } finally {
                        this.setState({ updatingEmail: false })
                      }
                    }}
                  >
                    {this.state.emailVerificationFailed ||
                    (!this.props.userSettings.emailVerified &&
                      this.props.userSettings.email !== null &&
                      this.props.userSettings.email === this.state.email)
                      ? 'Resend verification'
                      : 'Update'}
                  </SpinnerButton>
                </div>
                {this.state.emailVerificationFailed ? (
                  <p>Request failed, please try again.</p>
                ) : !this.state.updatingEmail &&
                  (this.state.emailVerificationRequested ||
                    (!this.props.userSettings.emailVerified &&
                      this.props.userSettings.email !== null &&
                      this.props.userSettings.email === this.state.email)) ? (
                  <p>
                    {this.state.emailVerificationRequested ? 'Verification email sent.' : ''} Please verify your email
                    address to receive notifications.
                  </p>
                ) : null}
                <h4>Get notifications for search:</h4>
                <div className="input-layout">
                  <input
                    className="text-input text-input-large text-input-dark"
                    disabled={
                      this.state.updatingNotifications ||
                      this.state.updatingEmail ||
                      !this.props.userSettings.emailVerified
                    }
                    value={this.state.notificationSearch}
                    onChange={(e) => {
                      this.setState({ notificationSearch: e.target.value })
                    }}
                  />
                  <SpinnerButton
                    size={'large'}
                    disabled={
                      this.state.updatingNotifications ||
                      this.state.updatingEmail ||
                      !this.props.userSettings.emailVerified
                    }
                    loading={this.state.updatingNotifications}
                    icon={'bell'}
                    onClick={async () => {
                      await this.props.onRequestNotificationUpdate(
                        this.props.stores.map(({ storeName }) => ({
                          op: 'add',
                          storeName,
                          text: this.state.notificationSearch,
                        })),
                      )
                      this.setState({ notificationSearch: '' })
                    }}
                  >
                    Subscribe
                  </SpinnerButton>
                </div>
              </label>
              <h4>Search notification subscriptions</h4>
              <ul className="no-style-list follow-list">
                {this.props.notifications.map(({ id, text, storeName, displayName }) => {
                  const displayText = displayName || text
                  return (
                    <li key={`${id}-${storeName}`}>
                      <span className={'button pill pill-button'}>
                        <span className={'pill-button-contents'}>
                          <NavLink to={`/search/?q=${text}`} title={`Search for "${text}"`}>
                            {displayText}
                          </NavLink>{' '}
                          <button
                            disabled={this.state.updatingNotifications}
                            onClick={async (e) => {
                              e.stopPropagation()
                              this.setState({ updatingNotifications: true })
                              await this.props.onRequestNotificationUpdate([
                                {
                                  op: 'remove',
                                  storeName,
                                  text,
                                },
                              ])

                              this.setState({ updatingNotifications: false })
                            }}
                            title={`Unsubscribe from "${displayText}"`}
                          >
                            <span aria-hidden="true" className={`store-icon store-icon-${storeName.toLowerCase()}`} />{' '}
                            <FontAwesomeIcon icon="times-circle" />
                          </button>
                        </span>
                      </span>
                    </li>
                  )
                })}
              </ul>
              <h4>Audio sample uploads</h4>
              <p style={{ fontSize: '90%', marginBottom: '10px' }}>
                Upload audio samples (WAV or MP3, max 10MB) to use for notification search by audio similarity. Uploaded
                samples will be processed at a later point to extract audio fingerprints. You will receive a
                notification email if a track matching the audio fingerprint is found in or added to the database.
              </p>
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="file"
                  accept="audio/wav,audio/wave,audio/x-wav,audio/mpeg,audio/mp3,.wav,.mp3"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      this.uploadAudioSample(file)
                      e.target.value = ''
                    }
                  }}
                  disabled={this.state.uploadingAudioSample || !this.props.userSettings.emailVerified}
                  style={{ display: 'none' }}
                  id="audio-sample-upload"
                />
                <label htmlFor="audio-sample-upload">
                  <SpinnerButton
                    size={'large'}
                    disabled={this.state.uploadingAudioSample || !this.props.userSettings.emailVerified}
                    loading={this.state.uploadingAudioSample}
                    icon={'upload'}
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('audio-sample-upload')?.click()
                    }}
                  >
                    Upload Audio Sample
                  </SpinnerButton>
                </label>
              </div>
              {this.state.audioSamples.length > 0 ? (
                <ul className="no-style-list follow-list">
                  {this.state.audioSamples.map((sample) => {
                    const isPlaying = this.state.playingAudioSample === sample.id
                    return (
                      <li key={sample.id} style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <span className={'button pill pill-button'}>
                            <span className={'pill-button-contents'}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const audioId = `audio-${sample.id}`
                                  const audio = document.getElementById(audioId)
                                  if (audio) {
                                    if (isPlaying) {
                                      audio.pause()
                                      this.setState({ playingAudioSample: null })
                                    } else {
                                      this.state.audioSamples.forEach((s) => {
                                        const otherAudio = document.getElementById(`audio-${s.id}`)
                                        if (otherAudio && s.id !== sample.id) {
                                          otherAudio.pause()
                                        }
                                      })
                                      audio.play()
                                      this.setState({ playingAudioSample: sample.id })
                                    }
                                  }
                                }}
                                title={isPlaying ? 'Pause' : 'Play'}
                                style={{
                                  marginRight: '8px',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: 'inherit',
                                }}
                              >
                                <FontAwesomeIcon icon={isPlaying ? 'pause' : 'play'} />
                              </button>
                              <span style={{ marginRight: '10px' }}>
                                {sample.filename || `Audio Sample ${sample.id}`}{' '}
                                <span style={{ fontSize: '85%', opacity: 0.8 }}>
                                  ({sample.fileType.split('/')[1]?.toUpperCase() || 'AUDIO'},{' '}
                                  {(sample.fileSize / 1024 / 1024).toFixed(2)}MB)
                                </span>
                              </span>
                              {typeof sample.matchCount === 'number' && sample.matchCount > 0 ? (
                                <>
                                  <span aria-hidden="true" style={{ opacity: 0.4, marginRight: '6px' }}>
                                    ·
                                  </span>
                                  <NavLink
                                    to={`/search/?q=sample:~${sample.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="suspected-matches-link"
                                    style={{
                                      fontSize: '85%',
                                      color: 'inherit',
                                      marginRight: '8px',
                                    }}
                                  >
                                    {sample.matchCount === 1
                                      ? '1 suspected match'
                                      : `${sample.matchCount} suspected matches`}
                                  </NavLink>
                                </>
                              ) : null}
                              <button
                                disabled={this.state.deletingAudioSample === sample.id}
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  const audio = document.getElementById(`audio-${sample.id}`)
                                  if (audio) {
                                    audio.pause()
                                  }
                                  if (this.state.playingAudioSample === sample.id) {
                                    this.setState({ playingAudioSample: null })
                                  }
                                  if (window.confirm('Delete this audio sample?')) {
                                    await this.deleteAudioSample(sample.id)
                                  }
                                }}
                                title="Delete audio sample"
                              >
                                <FontAwesomeIcon icon="times-circle" />
                              </button>
                            </span>
                          </span>
                          <audio
                            id={`audio-${sample.id}`}
                            src={sample.url}
                            onEnded={() => {
                              this.setState({ playingAudioSample: null })
                            }}
                            onPause={() => {
                              if (this.state.playingAudioSample === sample.id) {
                                this.setState({ playingAudioSample: null })
                              }
                            }}
                            onPlay={() => {
                              this.setState({ playingAudioSample: sample.id })
                            }}
                            style={{ display: 'none' }}
                          />
                        </div>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p style={{ fontSize: '90%', fontStyle: 'italic' }}>No audio samples uploaded yet.</p>
              )}
            </>
          ) : null}
          {this.state.page === 'ignores' ? (
            <>
              <h4>Artists ({this.state.artistIgnores.length})</h4>
              <ul className="no-style-list follow-list">
                {this.state.artistIgnores.map(({ name, id }) => (
                  <li key={id}>
                    <span className="button pill pill-button">
                      <span className="pill-button-contents">
                        {name}{' '}
                        <button
                          disabled={this.state.updatingArtistIgnores}
                          onClick={async () => {
                            this.setState({ updatingArtistIgnores: true })
                            await requestWithCredentials({
                              path: `/me/ignores/artists/${id}`,
                              method: 'DELETE',
                            })
                            await this.updateArtistIgnores()
                            this.setState({ updatingArtistIgnores: false })
                          }}
                        >
                          <FontAwesomeIcon icon="times-circle" />
                        </button>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <h4>Labels ({this.state.labelIgnores.length})</h4>
              <ul className="no-style-list follow-list">
                {this.state.labelIgnores.map(({ name, id }) => (
                  <li key={id}>
                    <span className="button pill pill-button">
                      <span className="pill-button-contents">
                        {name}{' '}
                        <button
                          disabled={this.state.updatingLabelIgnores}
                          onClick={async () => {
                            this.setState({ updatingLabelIgnores: true })
                            await requestWithCredentials({
                              path: `/me/ignores/labels/${id}`,
                              method: 'DELETE',
                            })
                            await this.updateLabelIgnores()
                            this.setState({ updatingLabelIgnores: false })
                          }}
                        >
                          <FontAwesomeIcon icon="times-circle" />
                        </button>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <h4>Artists on labels ({this.state.artistOnLabelIgnores.length})</h4>
              <ul className="no-style-list follow-list">
                {this.state.artistOnLabelIgnores.map(({ artist, label }) => (
                  <li key={`${artist.id}-${label.id}`}>
                    <span className="button pill pill-button">
                      <span className="pill-button-contents">
                        {artist.name} on ${label.name}
                        <button
                          disabled={this.state.updatingArtistOnLabelIgnores}
                          onClick={async () => {
                            this.setState({ updatingArtistOnLabelIgnores: true })
                            await requestWithCredentials({
                              path: `/me/ignores/artists-on-labels/`,
                              method: 'PATCH',
                              body: { op: 'delete', artistId: artist.id, labelId: label.id },
                            })
                            await this.updateArtistOnLabelIgnores()
                            this.setState({ updatingArtistOnLabelIgnores: false })
                          }}
                        >
                          <FontAwesomeIcon icon="times-circle" />
                        </button>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {this.state.page === 'collection' ? (
            <>
              <p>
                New tracks: {this.props.newTracks}
                <br />
                Total: {this.props.totalTracks}
                <br />
              </p>
              <h4>Mark tracks heard</h4>
              <p>
                Danger zone! This action cannot be undone!
                <br />
              </p>
              <p className="input-layout">
                {this.markHeardButton('Tracks older than a month', '1 months')}
                {this.markHeardButton('Tracks older than two months', '2 months')}
                {this.markHeardButton('Tracks older than half a year', '6 months')}
                {this.markHeardButton('Tracks older than one year', '1 years')}
                {this.markHeardButton('Tracks older than two years', '2 years')}
              </p>
              <p className="input-layout">{this.markHeardButton('All tracks', '0')}</p>
            </>
          ) : null}
          {this.state.page === 'player' ? (
            <>
              <h4>Fast forward button behaviour</h4>
              <p>
                Configure how the next and previous media buttons on your device (keyboard, headphones, etc.) behave. In{' '}
                <em>Seek</em> mode a single press seeks within the current track and a double press skips to the next or
                previous track. In <em>Skip</em> mode a single press skips tracks. This setting is stored on this
                device, so it can be configured separately for each device you use.
              </p>
              <p style={{ display: 'flex', alignItems: 'center', gap: 16 }} className="input-layout">
                <label htmlFor="media-button-behavior" className="noselect">
                  Fast forward button behaviour:
                </label>
                <ToggleButton
                  id="media-button-behavior"
                  checked={this.state.mediaButtonBehavior === 'skip'}
                  onChange={(state) => {
                    const behavior = state ? 'skip' : 'seek'
                    localStorage.setItem('mediaButtonBehavior', behavior)
                    this.setState({ mediaButtonBehavior: behavior })
                  }}
                />
                <span className="noselect">{this.state.mediaButtonBehavior === 'skip' ? 'Skip' : 'Seek'}</span>
              </p>
              <h4>Add to cart with play/pause</h4>
              <p>
                When enabled, pressing the play/pause media button (keyboard, headphones, etc.) twice within the
                configured delay adds the currently playing track to the selected cart. These settings are stored on
                this device, so they can be configured separately for each device you use.
              </p>
              <p style={{ display: 'flex', alignItems: 'center', gap: 16 }} className="input-layout">
                <label htmlFor="play-pause-add-to-cart" className="noselect">
                  Add to cart on double press:
                </label>
                <ToggleButton
                  id="play-pause-add-to-cart"
                  checked={this.state.playPauseAddToCart}
                  onChange={(state) => {
                    localStorage.setItem('playPauseDoubleClickAddToCart', state ? 'true' : 'false')
                    this.setState({ playPauseAddToCart: state })
                  }}
                />
                <span className="noselect">{this.state.playPauseAddToCart ? 'On' : 'Off'}</span>
              </p>
              {this.state.playPauseAddToCart ? (
                <>
                  <p style={{ display: 'flex', alignItems: 'center', gap: 16 }} className="input-layout">
                    <label className="noselect">Add tracks to cart:</label>
                    <CartSelectorDropDownButton
                      carts={this.props.carts}
                      selectedCartId={this.state.playPauseAddToCartCartId}
                      onSelectCart={(cartId) => {
                        if (cartId) {
                          localStorage.setItem('playPauseDoubleClickCartId', cartId)
                        } else {
                          localStorage.removeItem('playPauseDoubleClickCartId')
                        }
                        this.setState({ playPauseAddToCartCartId: cartId })
                      }}
                      onCreateCartClick={async (name) => {
                        const created = await this.props.onCreateCart(name)
                        await this.props.onUpdateCarts()
                        return created
                      }}
                    />
                  </p>
                  <p style={{ display: 'flex', alignItems: 'center', gap: 16 }} className="input-layout">
                    <label htmlFor="play-pause-add-to-cart-delay" className="noselect">
                      Maximum delay between presses (ms):
                    </label>
                    <input
                      id="play-pause-add-to-cart-delay"
                      type="number"
                      min="100"
                      step="100"
                      value={this.state.playPauseAddToCartDelay}
                      onChange={(e) => {
                        const value = e.target.value
                        this.setState({ playPauseAddToCartDelay: value })
                        const parsed = parseInt(value, 10)
                        if (!Number.isNaN(parsed) && parsed >= 100) {
                          localStorage.setItem('playPauseDoubleClickDelay', String(parsed))
                        }
                      }}
                    />
                  </p>
                </>
              ) : null}
            </>
          ) : null}
          {this.state.page === 'integrations' ? (
            <>
              <p>
                Fomo Player can synchronize your cart contents with Spotify playlists and import followed artists as
                well as playlists from Spotify.
              </p>
              <p>
                To enable synchronization of Spotify playlists with Fomo Player carts and to export followed artists to
                Spotify, you need to grant Fomo Player read and write rights in order to create playlists for the carts
                and to manage those playlists and the artist follows in your Spotify account. If however you do not need
                the synchronization and export, or are afraid of granting write access, you can grant only read access
                to enable importing playlists and followed artists from Spotify.
              </p>
              <p>
                The authorize links below will take you to a Spotify page where you can grant the rights. After granting
                the rights, you will be taken back to Fomo Player and you can enable cart synchronization in the{' '}
                <a style={{ textDecoration: 'underline' }} onClick={this.onShowPage.bind(this, 'carts')}>
                  Carts tab
                </a>{' '}
                in the settings.
              </p>
              <h4>Spotify</h4>
              {spotifyAuthorization ? (
                <>
                  <p>
                    <SpinnerButton
                      onClick={this.onDeauthorizeSpotifyClicked.bind(this)}
                      loading={this.state.deauthorizingSpotify}
                    >
                      Revoke authorization
                    </SpinnerButton>
                    <RevokeWarning />
                  </p>
                  <h5>Re-authorize</h5>
                  <p style={{ fontSize: '75%', marginTop: 5 }}>
                    Use this if you want to enable or disable write access to Spotify playlists.
                  </p>
                  <AuthorizationButtons hasWriteAccess={spotifyAuthorization?.has_write_access} />
                  <h5>Export followed artists</h5>
                  <p>
                    <SpinnerButton
                      loading={this.state.exportingFollowedArtists}
                      onClick={async () => {
                        this.setState({ exportingFollowedArtists: true, followedArtistsExportSuccess: null })
                        try {
                          const followedArtists = await requestJSONwithCredentials({
                            path: `/me/follows/artists`,
                            method: 'GET',
                          })

                          await requestWithCredentials({
                            path: `/stores/spotify/my-followed-artists`,
                            method: 'POST',
                            body: followedArtists
                              .filter(({ store: { name } }) => name === 'Spotify')
                              .map(({ url }) => url),
                          })
                          this.setState({ followedArtistsExportSuccess: true })
                        } catch (e) {
                          console.error('Error exporting followed artists', e)
                          this.setState({ followedArtistsExportSuccess: false })
                        } finally {
                          this.setState({ exportingFollowedArtists: false })
                        }
                      }}
                    >
                      Export followed artists to Spotify
                    </SpinnerButton>
                    {this.state.followedArtistsExportSuccess !== null && (
                      <div style={{ fontSize: '75%', marginTop: 5 }}>
                        {this.state.followedArtistsExportSuccess === true
                          ? 'Artist follows exported successfully'
                          : 'Exporting followed artists failed. Please try again.'}
                      </div>
                    )}
                  </p>
                  <h5>Import followed artists</h5>
                  <p>
                    <SpinnerButton
                      loading={this.state.importingFollowedArtists}
                      onClick={async () => {
                        this.setState({ importingFollowedArtists: true, importedArtists: [] })
                        try {
                          const followedArtists = await requestJSONwithCredentials({
                            path: `/stores/spotify/my-followed-artists`,
                            method: 'GET',
                          })

                          const importedArtists = await requestJSONwithCredentials({
                            path: `/me/follows/artists`,
                            method: 'POST',
                            body: followedArtists,
                          })
                          await this.updateArtistFollows()
                          this.setState({ importedArtists })
                        } catch (e) {
                          console.error('Error importing followed artists', e)
                          this.setState({ followedArtistsImportFailed: true })
                        } finally {
                          this.setState({ importingFollowedArtists: false })
                        }
                      }}
                    >
                      {this.state.importedArtists ? 'Refresh' : 'Import'} followed artists from Spotify
                    </SpinnerButton>
                    {this.state.followedArtistsImportFailed && (
                      <div style={{ fontSize: '75%', marginTop: 5 }}>
                        Importing followed artists failed. Please try again.
                      </div>
                    )}
                    {this.state.importedArtists.length > 0 && (
                      <h6>{this.state.importedArtists.length} artists imported:</h6>
                    )}
                    {this.state.importedArtists.map(({ name, url }) => {
                      return (
                        <FollowedItem
                          disabled={this.state.updatingArtistFollows}
                          onUnfollowClick={async () => {
                            this.setState({ updatingArtistFollows: true })
                            await requestWithCredentials({
                              path: `/me/follows/artists/${storeArtistId}`,
                              method: 'DELETE',
                            })
                            await this.updateArtistFollows()
                            this.setState({ updatingArtistFollows: false })
                          }}
                          {...{ storeName: 'spotify', title: name, starred: false, url }}
                        />
                      )
                    })}
                  </p>
                  <h5>Import playlists</h5>
                  <p>
                    <SpinnerButton
                      loading={this.state.fetchingPlaylists}
                      onClick={() => {
                        this.setState({ fetchingPlaylists: true, importedPlaylists: null })
                        requestJSONwithCredentials({
                          path: `/stores/spotify/my-playlists`,
                          method: 'GET',
                        })
                          .then((playlists) => {
                            this.setState({ spotifyPlaylists: playlists })
                          })
                          .finally(() => this.setState({ fetchingPlaylists: false }))
                      }}
                    >
                      {this.state.spotifyPlaylists ? 'Refresh' : 'Get'} Spotify playlists
                    </SpinnerButton>
                    {this.state.spotifyPlaylists?.length === 0 ? (
                      <h5>No playlists found</h5>
                    ) : (
                      this.state.spotifyPlaylists && <h5>Available playlists</h5>
                    )}
                    <ul className={'no-style-list'} style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {this.state.spotifyPlaylists?.map(({ id, url, name, img }) => (
                        <li key={id ?? url}>
                          <ImportPlaylistButton
                            id={id}
                            name={name}
                            storeName="spotify"
                            type="playlist"
                            url={url}
                            img={img}
                            loading={this.state.importingPlaylist === url}
                            disabled={this.state.importingPlaylist !== null}
                            onClick={(() => this.onImportPlaylistItemClick(url)).bind(this)}
                            data-onboarding-id="follow-item"
                          />
                        </li>
                      ))}
                    </ul>
                  </p>
                </>
              ) : (
                <>
                  <h5>Authorize</h5>
                  <AuthorizationButtons />
                  <p>
                    <strong>Note</strong>: Even when the authorization is successful, you will not be redirected back to
                    this view (this feature is work in progress). After you return to the player, just reopen the
                    Integrations tab.
                  </p>
                </>
              )}
            </>
          ) : null}
        </div>
      </div>
    )
  }

  async onDeauthorizeSpotifyClicked() {
    try {
      this.setState({ deauthorizingSpotify: true })
      await requestWithCredentials({
        method: 'DELETE',
        path: `/me/authorizations/spotify`,
      })
      this.setState({ authorizations: [] })
    } catch (e) {
      console.error('Removing authorization failed', e)
    } finally {
      this.setState({ deauthorizingSpotify: false })
    }
  }

  async onFollowItemClick(url, name, type) {
    try {
      this.setState({ updatingFollowWithUrl: url })
      const props = {
        body: [{ url, name }],
      }

      await requestJSONwithCredentials({
        path: `/me/follows/${type}s`,
        method: 'POST',
        ...props,
      })

      await this.updateFollows()
      if (Onboarding.active && Onboarding.isCurrentStep(Onboarding.steps.FollowButton)) {
        this.clearSearch()
        Onboarding.moveToNextWhenItemVisible(`[data-onboarding-id="follow-item"]`)
      }
    } catch (e) {
      console.error(e)
    } finally {
      this.setState({ updatingFollowWithUrl: null })
    }
  }

  getFollowItemDisabled(type, { id, url, storeName }) {
    if (this.state.updatingFollowWithUrl !== null) {
      return true
    }

    if (type === 'playlist') {
      return this.state.playlistFollows.some(
        ({ id: followId, playlistStoreId, storeName: followStoreName, url: followUrl }) =>
          (followId && id && String(followId) === String(id)) ||
          (playlistStoreId && id && String(playlistStoreId) === String(id)) ||
          (followUrl && url && followUrl === url) ||
          (followStoreName && storeName && followStoreName === storeName && followUrl === url),
      )
    }

    const follows = type === 'artist' ? this.state.artistFollows : this.state.labelFollows
    const storeIdProperty = type === 'artist' ? 'storeArtistId' : 'storeLabelId'
    return follows.some((follow) => {
      const followStoreName = follow.store?.name
      const followStoreId = follow[storeIdProperty]
      return (
        (follow.url && url && follow.url === url) ||
        (followStoreId && id && String(followStoreId) === String(id)) ||
        (followStoreName &&
          storeName &&
          followStoreName === storeName &&
          followStoreId &&
          id &&
          String(followStoreId) === String(id))
      )
    })
  }

  async onImportPlaylistItemClick(url) {
    this.setState({ importingPlaylist: url })
    try {
      await requestJSONwithCredentials({
        url: `${apiURL}/me/carts`,
        method: 'POST',
        body: { url },
      })
      await this.props.onUpdateCarts()
      this.setState({ importedPlaylists: [...this.state.importedPlaylists, url] })
    } catch (e) {
      console.error('Error importing playlist', e)
      throw e
    } finally {
      this.setState({ importingPlaylist: null })
    }
  }
}

export default withRouter(Settings)
